import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Alert, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ProcessStatusTracker from './ProcessStatusTracker';
import { getProcessErrorMessage } from '../utils/processDisplay';

const isAndroidPWA = () => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    const isAndroid = /android/i.test(navigator.userAgent);
    return isPWA && isAndroid;
};

const openNotionPage = (e, pageId) => {
    const notionPath = `itamarco/${pageId.replaceAll('-', '')}`;
    const notionUrl = `https://www.notion.so/${notionPath}`;

    if (isAndroidPWA()) {
        e.preventDefault();
        // Android Intent URL: tells Chrome to dispatch to Notion app directly,
        // bypassing Chrome's own URL handling. Falls back to browser if app not installed.
        window.location.href = `intent://www.notion.so/${notionPath}#Intent;scheme=https;package=notion.id;S.browser_fallback_url=${encodeURIComponent(notionUrl)};end`;
    } else if (window.matchMedia('(display-mode: standalone)').matches) {
        e.preventDefault();
        window.location.href = notionUrl;
    }
    // In regular browser: let the default <a target="_blank"> behavior work
};

const ProcessList = ({ processes, stepsArray, isMobile, MAX_DISPLAYED_PROCESSES = 3 }) => {
    const theme = useTheme();

    if (!processes || processes.length === 0) {
        return null;
    }

    return (
        <Card sx={{ mt: 2, mb: 4 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    Recent Processes
                </Typography>
                {processes.slice(0, MAX_DISPLAYED_PROCESSES).map((process) => {
                    const errorMessage = getProcessErrorMessage(process);

                    return (
                        <Card
                        key={process._id} 
                        sx={{ 
                            mb: 3,
                            p: 3,
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            '&:hover': {
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }
                        }}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start', 
                            mb: 2,
                            flexWrap: 'wrap',
                            gap: 1
                        }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    {process.fileName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {new Date(process.createdAt).toLocaleString()}
                                </Typography>
                            </Box>
                            <Chip 
                                label={process.status} 
                                color={
                                    process.status === 'completed' ? 'success' :
                                    process.status === 'error' ? 'error' :
                                    process.status === 'queued' ? 'info' :
                                    'warning'
                                }
                                sx={{
                                    textTransform: 'capitalize',
                                    fontWeight: 500,
                                    px: 1,
                                    minWidth: 90,
                                    height: 32
                                }}
                            />
                        </Box>
                        
                        <ProcessStatusTracker 
                            process={process} 
                            stepsArray={stepsArray} 
                            isMobile={isMobile} 
                        />

                        {errorMessage && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mt: 2,
                                    '& .MuiAlert-message': {
                                        width: '100%'
                                    }
                                }}
                            >
                                {errorMessage}
                            </Alert>
                        )}
                        {process.result?.pageId && (
                            <Box sx={{ 
                                mt: 3,
                                display: 'flex', 
                                gap: 2,
                                flexWrap: 'wrap'
                            }}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    href={`https://www.notion.so/itamarco/${process.result.pageId.replaceAll('-', '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => openNotionPage(e, process.result.pageId)}
                                    sx={{ 
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 500
                                    }}
                                >
                                    View in Notion
                                </Button>
                                {process.result.conversationId && (
                                    <Button
                                        variant="outlined"
                                        size="medium"
                                        href={`/chat/${process.result.conversationId}`}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 500
                                        }}
                                    >
                                        Open Chat
                                    </Button>
                                )}
                                {process.result.presentationUrl && (
                                    <Button
                                        variant="outlined"
                                        size="medium"
                                        href={process.result.presentationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            color: 'secondary.main',
                                            borderColor: 'secondary.main'
                                        }}
                                    >
                                        View Presentation
                                    </Button>
                                )}
                            </Box>
                        )}
                        </Card>
                    );
                })}
            </CardContent>
        </Card>
    );
};

export default ProcessList;
